import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormImageUploadMorden from "../../components/formFields/formImageUploadMorden";
import {
  CoreServicesDetailsImageUpload,
  createCoreServices,
  getCoreServicesByID,
  updateCoreServices,
} from "./services/coreservices.services";
import { clearCoreServiceDetails } from "./coreservicesSlice";
import { slugify } from "../../_helper/helper";
import FormTextArea from "../../components/formFields/FormTextArea";

const CreateCoreServices: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const CoreServicesID = location.state?.ID;
  const CoreServicesDetail = useAppSelector(
    (state) => state.coreservices.CoreServiceDetail,
  );
  const loading = useAppSelector((state) => state.coreservices.loading);
  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    if (CoreServicesID) {
      dispatch(getCoreServicesByID(CoreServicesID));
    }
    return () => {
      dispatch(clearCoreServiceDetails());
    };
  }, [CoreServicesID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [CoreServicesDetail]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values.Title ?? "",
      Description: values.Description ?? "",
      URL: values.URL ?? "",
      MetaTitle: values.MetaTitle ? values.MetaTitle : "",
      MetaKeywords: values.MetaKeywords ? values.MetaKeywords : "",
      MetaDescription: values.MetaDescription ? values.MetaDescription : "",
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const CoreServicesImage =
      values?.Image && values?.Image.length && typeof values?.Image !== "string"
        ? values.Image[0].getRawFile()
        : null;
    try {
      if (CoreServicesID) {
        const editPayload = {
          ...formData,
          ID: CoreServicesID,
        };

        const response = await dispatch(updateCoreServices(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (values?.Image && typeof values?.Image !== "string") {
            ImageData.append("Image", CoreServicesImage);
            ImageData.append("ID", CoreServicesID);
            const imageResponse = await dispatch(
              CoreServicesDetailsImageUpload(ImageData),
            );
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/coreservices");
            } else {
              navigate("/coreservices");
            }
          } else {
            navigate("/coreservices");
          }
        }
      } else {
        const response = await dispatch(createCoreServices(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.Image &&
            typeof values?.Image !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Image", CoreServicesImage);
            ImageData.append("ID", response?.payload?.Data?.ID);
            const imageResponse = await dispatch(
              CoreServicesDetailsImageUpload(ImageData),
            );
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/coreservices");
            } else {
              navigate("/coreservices");
            }
          } else {
            navigate("/coreservices");
          }
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("URL", { value: slug });
  };

  return (
    <>
      <div>
        {loading && <LoadingPanel gridRef={gridRef} />}
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={CoreServicesDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {CoreServicesID
                        ? "Update Core Service"
                        : "Create Core Service"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={1}>
                    <Field
                      name="Title"
                      label="Title"
                      placeholder="Title"
                      onChange={(e) => handleCreateSlug(e, formRenderProps)}
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={1}>
                    <Field
                      name="URL"
                      label="URL"
                      placeholder="URL"
                      astrike={true}
                      component={FormTextField}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      placeholder="i.e. Active"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="Description"
                      label="Description"
                      placeholder="Description"
                      component={FormRichTextField}
                      minHeight={100}
                    />
                  </GridLayoutItem>
                  <GridLayout>
                    <GridLayoutItem
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <Field
                        justifyContent="start"
                        name={`Image`}
                        label="Image"
                        type="file"
                        accept="image/*"
                        alternateName="CoreServices Image"
                        component={FormImageUploadMorden}
                        style={{ height: 300 }}
                      />
                    </GridLayoutItem>
                  </GridLayout>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="MetaTitle"
                      label="Meta Title "
                      placeholder="Meta Title"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="MetaKeywords"
                      label="Meta Key Words"
                      placeholder="Meta Key words"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3} style={{ width: "100%" }}>
                    <Field
                      name="MetaDescription"
                      label="Meta Description"
                      component={FormTextArea}
                      placeholder="Meta Description"
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      marginTop: 10,
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={CoreServicesID ? "Update" : "Create"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/coreservices")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateCoreServices;
